import React from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  HStack,
  Icon,
  Progress,
  Skeleton,
  Spinner,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import { FiCheckCircle, FiPlay, FiRefreshCw, FiXCircle } from 'react-icons/fi';
import { SaktiSyncEntity, SaktiSyncEntityProgress, SaktiSyncJob, SaktiSyncStatus } from '../types';
import { SAKTI_UI_SIZE } from '../uiSizes';

interface SaktiSyncPanelProps {
  entities: SaktiSyncEntity[];
  loadingEntities: boolean;
  entitiesError: string | null;
  selected: string[];
  job: SaktiSyncJob | null;
  isSyncing: boolean;
  onToggleEntity: (name: string) => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
  onSyncAll: () => void;
  onSyncSelected: () => void;
  onReloadEntities: () => void;
  subtitle?: string;
}

const formatDuration = (ms: number): string => {
  if (ms <= 0) return '—';
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
};

const TERMINAL_STATUSES: SaktiSyncStatus[] = ['completed', 'failed'];

const StatusIndicator: React.FC<{ status: SaktiSyncStatus }> = ({ status }) => {
  switch (status) {
    case 'completed':
      return <Icon as={FiCheckCircle} color="green.500" />;
    case 'failed':
      return <Icon as={FiXCircle} color="red.500" />;
    case 'running':
      return <Spinner size="xs" color="customGreen.500" />;
    default:
      return <Box w="10px" h="10px" borderRadius="full" bg="gray.300" />;
  }
};

const EntityProgressRow: React.FC<{ entity: SaktiSyncEntityProgress }> = ({ entity }) => (
  <HStack justify="space-between" py={1.5} px={1} spacing={3}>
    <HStack spacing={2} minW={0}>
      <StatusIndicator status={entity.status} />
      <Text fontSize="sm" noOfLines={1}>
        {entity.name}
      </Text>
    </HStack>
    <HStack spacing={3} flexShrink={0}>
      {entity.status === 'completed' && entity.count > 0 && (
        <Text fontSize="xs" color="gray.500">
          {entity.count.toLocaleString('id-ID')} baris
        </Text>
      )}
      {entity.status === 'failed' && entity.error && (
        <Tooltip label={entity.error} hasArrow>
          <Text fontSize="xs" color="red.500" maxW="200px" noOfLines={1}>
            {entity.error}
          </Text>
        </Tooltip>
      )}
      <Text fontSize="xs" color="gray.400" minW="48px" textAlign="right">
        {formatDuration(entity.duration_ms)}
      </Text>
    </HStack>
  </HStack>
);

const SaktiSyncPanel: React.FC<SaktiSyncPanelProps> = ({
  entities,
  loadingEntities,
  entitiesError,
  selected,
  job,
  isSyncing,
  onToggleEntity,
  onSelectAll,
  onClearSelection,
  onSyncAll,
  onSyncSelected,
  onReloadEntities,
  subtitle = 'Tarik data master terbaru dari Odoo',
}) => {
  const jobEntities = job?.entities ?? [];
  const doneCount = jobEntities.filter((entity) =>
    TERMINAL_STATUSES.includes(entity.status)
  ).length;
  const progressValue = jobEntities.length > 0 ? (doneCount / jobEntities.length) * 100 : 0;
  const isJobActive = isSyncing && jobEntities.length > 0;

  return (
    <Box
      bg="white"
      borderRadius={SAKTI_UI_SIZE.cardRadius}
      borderWidth="1px"
      borderColor="gray.200"
      p={SAKTI_UI_SIZE.cardPadding}
    >
      <VStack align="stretch" spacing={4}>
        <HStack justify="space-between" align="center" flexWrap="wrap" gap={2}>
          <VStack align="start" spacing={0}>
            <Text fontWeight="bold" fontSize={SAKTI_UI_SIZE.tabTitle} color="gray.900">
              Sinkronisasi Data Master
            </Text>
            <Text fontSize="xs" color="gray.500">
              {subtitle}
            </Text>
          </VStack>
          <HStack spacing={2}>
            <Button
              leftIcon={<FiPlay />}
              colorScheme="green"
              bg="customGreen.500"
              _hover={{ bg: 'customGreen.600' }}
              size="sm"
              onClick={onSyncAll}
              isLoading={isSyncing}
              loadingText="Sinkron..."
            >
              Sinkron Semua
            </Button>
            <Button
              leftIcon={<FiPlay />}
              variant="outline"
              size="sm"
              onClick={onSyncSelected}
              isDisabled={isSyncing || selected.length === 0}
            >
              Sinkron Terpilih ({selected.length})
            </Button>
          </HStack>
        </HStack>

        {entitiesError && (
          <Alert status="warning" borderRadius="md">
            <AlertIcon />
            <HStack justify="space-between" w="full">
              <Text fontSize="sm">{entitiesError}</Text>
              <Button size="xs" leftIcon={<FiRefreshCw />} onClick={onReloadEntities}>
                Coba lagi
              </Button>
            </HStack>
          </Alert>
        )}

        {/* Live job progress */}
        {job && (
          <Box bg="gray.50" borderRadius="md" p={3}>
            <HStack justify="space-between" mb={2}>
              <HStack spacing={2}>
                <Text fontSize="sm" fontWeight="semibold">
                  Status
                </Text>
                <Badge
                  colorScheme={
                    job.status === 'completed'
                      ? 'green'
                      : job.status === 'failed'
                      ? 'red'
                      : 'blue'
                  }
                >
                  {job.status}
                </Badge>
              </HStack>
              <Text fontSize="xs" color="gray.500">
                {doneCount}/{jobEntities.length} selesai
              </Text>
            </HStack>
            <Progress
              value={progressValue}
              size="sm"
              borderRadius="full"
              colorScheme={job.status === 'failed' ? 'red' : 'green'}
              isAnimated={isJobActive}
              hasStripe={isJobActive}
            />
            {job.error && (
              <Text fontSize="xs" color="red.500" mt={2}>
                {job.error}
              </Text>
            )}
            <VStack align="stretch" spacing={0} mt={2} divider={<Divider />}>
              {jobEntities.map((entity) => (
                <EntityProgressRow key={entity.name} entity={entity} />
              ))}
            </VStack>
          </Box>
        )}

        <Divider />

        {/* Entity picker */}
        <Box>
          <HStack justify="space-between" mb={2}>
            <Text fontSize="sm" fontWeight="semibold" color="gray.700">
              Pilih entitas (opsional)
            </Text>
            <HStack spacing={2}>
              <Button
                size="xs"
                variant="ghost"
                onClick={onSelectAll}
                isDisabled={isSyncing || entities.length === 0}
              >
                Pilih semua
              </Button>
              <Button
                size="xs"
                variant="ghost"
                onClick={onClearSelection}
                isDisabled={isSyncing || selected.length === 0}
              >
                Bersihkan
              </Button>
            </HStack>
          </HStack>

          <Skeleton isLoaded={!loadingEntities} borderRadius="md" minH="60px">
            <Flex wrap="wrap" gap={2}>
              {entities.length === 0 ? (
                <Text fontSize="sm" color="gray.500">
                  Tidak ada entitas tersedia.
                </Text>
              ) : (
                entities.map((entity) => (
                  <Box
                    key={entity.name}
                    borderWidth="1px"
                    borderColor={selected.includes(entity.name) ? 'customGreen.500' : 'gray.200'}
                    borderRadius="md"
                    px={3}
                    py={1.5}
                    bg={selected.includes(entity.name) ? 'green.50' : 'white'}
                  >
                    <Checkbox
                      isChecked={selected.includes(entity.name)}
                      onChange={() => onToggleEntity(entity.name)}
                      isDisabled={isSyncing}
                      colorScheme="green"
                    >
                      <Text fontSize="sm">{entity.name}</Text>
                    </Checkbox>
                  </Box>
                ))
              )}
            </Flex>
          </Skeleton>
        </Box>
      </VStack>
    </Box>
  );
};

export default SaktiSyncPanel;
